import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Paper, Stepper, Step, StepLabel, StepContent, CircularProgress, Alert, Chip } from "@mui/material";
import { Download as DownloadIcon, Extension as ExtensionIcon, NewReleases as VersionIcon } from "@mui/icons-material";

const config = window.AUTOMATRIX_CONFIG || {};
const API_BASE_URL = config.API_BASE_URL || "/api";

export default function ExtensionDownloadPage() {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${API_BASE_URL}/extension-download/info`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setVersion(data.version))
      .catch(() => {});
  }, []);

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API_BASE_URL}/extension-download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `automatrix-extension-v${version || "latest"}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 720, mx: "auto", py: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <ExtensionIcon sx={{ fontSize: 40, color: "primary.main" }} />
        <Typography variant="h4">Automatrix Chrome Extension</Typography>
      </Box>

      <Paper sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={handleDownload}
            disabled={downloading}
          >
            {downloading ? "Downloading..." : "Download Extension"}
          </Button>
          {version && (
            <Chip
              icon={<VersionIcon />}
              label={`v${version}`}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {version
            ? `automatrix-extension-v${version}.zip`
            : "automatrix-extension.zip"}
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Paper>

      <Typography variant="h6" sx={{ mb: 2 }}>Installation</Typography>
      <Stepper orientation="vertical" activeStep={-1}>
        <Step active>
          <StepLabel>Download and unzip</StepLabel>
          <StepContent>
            <Typography variant="body2">Click the button above, then unzip the downloaded file.</Typography>
          </StepContent>
        </Step>
        <Step active>
          <StepLabel>Open Chrome Extensions</StepLabel>
          <StepContent>
            <Typography variant="body2">
              Navigate to <code>chrome://extensions</code> and enable <strong>Developer mode</strong> (toggle in top-right).
            </Typography>
          </StepContent>
        </Step>
        <Step active>
          <StepLabel>Load the extension</StepLabel>
          <StepContent>
            <Typography variant="body2">
              Click <strong>Load unpacked</strong> and select the unzipped folder.
            </Typography>
          </StepContent>
        </Step>
        <Step active>
          <StepLabel>Start using</StepLabel>
          <StepContent>
            <Typography variant="body2">
              Click the Automatrix icon in your browser toolbar to open the sidebar. It syncs scripts and credentials from your vault automatically.
            </Typography>
          </StepContent>
        </Step>
      </Stepper>
    </Box>
  );
}
